import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { useAuth as useClerkAuth, useClerk } from "@clerk/clerk-react";
import { apiFetch, registerAuthFailureHandler, setTokenGetter } from "@/lib/api";
import { accountProblem, type AccountProblem } from "@/auth/account-problem";
import { employeeApi, type EmployeeSelf } from "@/lib/employee-api";

export type UserRole = "super_admin" | "tenant_admin";

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  roleType: UserRole;
  department: string;
  phone: string;
  /** Pre-formatted in the *viewer's* timezone — used by the platform console,
   *  which spans tenants. Tenant screens format `lastLoginAt` in tenant time. */
  lastLogin: string;
  /** Raw ISO timestamp, so tenant screens can render it in tenant time. */
  lastLoginAt: string | null;
  avatarInitials: string;
  tenantId: string | null;
  /** Raw backend role (TENANT_OWNER | HR_MANAGER | ...) for permission checks. */
  backendRoleType: string;
  /** Visibility scope (FEAT-2026-005): true = restricted to an org subtree. */
  scoped: boolean;
}

interface ImpersonationContext {
  tenantId: string;
  tenantName: string;
}

interface AuthContextValue {
  user: AdminUser | null;
  /**
   * The signed-in Clerk identity's **employee** record, when it has one
   * (FEAT-2026-013). Independent of `user`: one human can be both an admin
   * and an on-the-ground worker, and then both are set.
   */
  employee: EmployeeSelf | null;
  loading: boolean;
  /**
   * Set when Clerk authenticated the user but the backend let them in as
   * neither an admin nor an employee — and why: no GeoKerja record for their
   * email, a deactivated one, or an account that could not be loaded
   * (BUG-2026-018). Distinct from `user === null + !loading` so the UI can
   * explain it instead of bouncing back to /login (which would loop — Clerk
   * still says you're signed in).
   */
  linkError: AccountProblem | null;
  /** Sign-out (Clerk-managed). */
  logout: () => Promise<void>;
  /** Re-fetch the local profile (`/auth/me`). Useful after profile edits. */
  refreshMe: () => Promise<void>;
  impersonating: ImpersonationContext | null;
  /** TODO(phase-2): rebuild on top of Clerk sessions. Returns 501 today. */
  startImpersonation: (ctx: ImpersonationContext) => Promise<{ ok: boolean; error?: string }>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------- Backend DTOs ----------
interface BackendUser {
  id: string;
  email: string;
  username: string | null;
  name: string;
  roleType: string; // SUPER_ADMIN | TENANT_OWNER | HR_MANAGER | ...
  roleLabel: string | null;
  department: string | null;
  phone: string | null;
  tenantId: string | null;
  status: string;
  lastLoginAt: string | null;
  scopeDepartmentId?: string | null;
}

interface MeResponse {
  user: BackendUser;
  impersonatedTenant: { id: string; name: string; code: string } | null;
}

function toRoleType(backendRole: string): UserRole {
  return backendRole === "SUPER_ADMIN" ? "super_admin" : "tenant_admin";
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

function formatLastLogin(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return sameDay ? `Today, ${time}` : `${d.toLocaleDateString()} ${time}`;
}

function toAdminUser(bu: BackendUser): AdminUser {
  return {
    id: bu.id,
    name: bu.name,
    email: bu.email,
    role: bu.roleLabel ?? (bu.roleType === "SUPER_ADMIN" ? "Super Administrator" : "Administrator"),
    roleType: toRoleType(bu.roleType),
    department: bu.department ?? "",
    phone: bu.phone ?? "",
    lastLogin: formatLastLogin(bu.lastLoginAt),
    lastLoginAt: bu.lastLoginAt ?? null,
    avatarInitials: initials(bu.name),
    tenantId: bu.tenantId,
    backendRoleType: bu.roleType,
    scoped: !!bu.scopeDepartmentId,
  };
}

/**
 * Bridges Clerk's `useAuth().getToken` into the module-level token getter
 * used by `apiFetch`. Mounting this once near the app root is what makes
 * every subsequent `apiFetch` call automatically authenticated.
 *
 * Kept as an internal component (not exported) — `AuthProvider` always
 * mounts it, so consumers don't have to think about it.
 */
function ClerkBridge() {
  const { getToken } = useClerkAuth();
  useEffect(() => {
    // Clerk's getToken returns the current session JWT, refreshing it if
    // it's about to expire. We just plug that in as our bearer source.
    setTokenGetter(() => getToken());
  }, [getToken]);
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded } = useClerkAuth();
  const clerk = useClerk();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [employee, setEmployee] = useState<EmployeeSelf | null>(null);
  const [impersonating, setImpersonating] = useState<ImpersonationContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [linkError, setLinkError] = useState<AccountProblem | null>(null);

  const fetchMe = useCallback(async () => {
    // Resolve both principals at once. They are genuinely independent — the
    // backend picks User or Employee by URL prefix, and a single Clerk
    // identity can map to a row in either table or in both. Asking for both
    // is what lets an admin-who-is-also-staff use either lane, and what lets
    // an employee with no `User` row in at all (`/auth/me` 401s for them).
    const [adminResult, employeeResult] = await Promise.allSettled([
      apiFetch<MeResponse>("/auth/me"),
      employeeApi.me(),
    ]);

    if (adminResult.status === "fulfilled") {
      const me = adminResult.value;
      setUser(toAdminUser(me.user));
      setImpersonating(
        me.impersonatedTenant
          ? { tenantId: me.impersonatedTenant.id, tenantName: me.impersonatedTenant.name }
          : null,
      );
    } else {
      setUser(null);
      setImpersonating(null);
    }

    setEmployee(employeeResult.status === "fulfilled" ? employeeResult.value : null);

    // Only a signed-in Clerk identity that matches *neither* table is an
    // error. Failing one probe is normal: most admins are not employees, and
    // most employees are not admins.
    if (adminResult.status === "rejected" && employeeResult.status === "rejected") {
      // Usually an identity that should not have signed in at all: someone who
      // signed up on their own, when GeoKerja accounts are invitation-only.
      const problem = accountProblem(adminResult.reason, employeeResult.reason);
      console.warn(`GeoKerja account unavailable (${problem.kind}): ${problem.message}`);
      setLinkError(problem);
    } else {
      setLinkError(null);
    }
  }, []);

  useEffect(() => {
    // When Clerk signs the user out (e.g. session revoked from another
    // device), drop the local user. The redirect itself is handled by
    // Clerk's `<SignedOut>` guard on protected routes.
    registerAuthFailureHandler(() => {
      setUser(null);
      setEmployee(null);
      setImpersonating(null);
    });
  }, []);

  // Hydrate when Clerk auth state settles. Loading stays true until both
  // Clerk has loaded *and* (if signed in) we've fetched our local profile.
  useEffect(() => {
    let cancelled = false;
    if (!isLoaded) {
      setLoading(true);
      return;
    }
    if (!isSignedIn) {
      setUser(null);
      setEmployee(null);
      setImpersonating(null);
      setLinkError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchMe().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, fetchMe]);

  const logout = useCallback(async () => {
    // Flip `loading` on so Login.tsx renders <AuthSplash /> through the
    // signOut transition. Without this, there's a brief window where
    // Clerk still reports `isSignedIn = true` but our local user is
    // already cleared — which would otherwise fall through to the
    // "no admin account linked" branch for ~100ms.
    setLoading(true);
    setUser(null);
    setEmployee(null);
    setImpersonating(null);
    setLinkError(null);
    await clerk.signOut();
    // The hydrate effect picks up Clerk's new `isSignedIn=false` and
    // resets `loading=false` from there, so we don't reset it here.
  }, [clerk]);

  const refreshMe = useCallback(async () => {
    await fetchMe();
  }, [fetchMe]);

  const startImpersonation: AuthContextValue["startImpersonation"] = async () => {
    // TODO(phase-2): wire to a Clerk-aware impersonation token.
    return { ok: false, error: "Impersonation is not available in the Clerk migration phase" };
  };
  const stopImpersonation = async () => {
    /* noop — see startImpersonation */
  };

  const value = useMemo<AuthContextValue>(
    () => ({ user, employee, loading, linkError, logout, refreshMe, impersonating, startImpersonation, stopImpersonation }),
    [user, employee, loading, linkError, logout, refreshMe, impersonating],
  );

  return (
    <AuthContext.Provider value={value}>
      <ClerkBridge />
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

/**
 * Compatibility shim for the old `useAuth().login(...)` API. Clerk owns
 * sign-in now (rendered via `<SignIn />` on the login route) — exporting
 * this stub for any component that still calls `login` would be a footgun;
 * better to fail loudly so the call site can be migrated.
 *
 * Use Clerk's hooks instead:
 *   import { useSignIn } from "@clerk/clerk-react";
 */
export function useLegacyLoginRemoved(): never {
  throw new Error(
    "useAuth().login was removed in the Clerk migration — use @clerk/clerk-react's <SignIn /> or useSignIn() hook",
  );
}

/**
 * Re-export convenient Clerk error type so callers that previously did
 * `instanceof ApiError` for sign-in errors don't get stuck.
 */
export { ApiError } from "@/lib/api";
